import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { format } from 'date-fns'

import Loader from '../components/Loader'
import UrgencyBadge from '../components/tickets/UrgencyBadge'
import useTickets from '../hooks/useTickets'

// TicketsPage (design: Pages and UI components; Req 3.1, 3.2)
//
// List view for tickets. Customers see only their organization's tickets and
// admins see every ticket with an added Customer column (client scoping is
// handled by useTickets and mirrored server-side by security rules). Users with
// no resolved role fall through to the "access pending" state (Error Handling:
// Unresolved role, Req 8.6).

// Normalize a createdAt value (Firestore Timestamp or JS Date) to a formatted
// string. Returns an empty string when the value is missing or invalid.
const formatCreatedAt = (createdAt) => {
  const date = createdAt?.toDate ? createdAt.toDate() : createdAt
  const isValidDate = date instanceof Date && !isNaN(date)
  return isValidDate ? format(date, 'PP') : ''
}

// Truncate a description for the list view so long text does not blow out rows.
const truncate = (text = '', max = 80) => {
  if (text.length <= max) return text
  return `${text.slice(0, max)}…`
}

const TicketsPage = ({ role, customer }) => {
  const admin = role === 'admin'
  const customerId = customer?.id || null

  const [showClosed, setShowClosed] = useState(false)

  const { data, loading } = useTickets({ admin, customerId })

  // Unresolved role: neither an admin nor a customer with an id.
  if (!admin && !customerId) {
    return (
      <div className='p-4 max-w-4xl mx-auto'>
        <p className='text-gray-600'>
          Your access is pending. Please contact support.
        </p>
      </div>
    )
  }

  if (loading) {
    return (
      <div className='p-4'>
        <Loader />
      </div>
    )
  }

  const canCreate = admin || !!customerId

  // Hide Closed tickets by default; the toggle reveals them.
  const visibleTickets = showClosed
    ? data
    : data.filter((t) => t.status !== 'Closed')

  return (
    <div className='p-4 max-w-5xl mx-auto text-left'>
      <div className='mb-5 flex items-center justify-between'>
        <div>
          <h1 className='text-3xl font-bold text-gray-900'>Support Tickets</h1>
          <p className='mt-1 text-sm text-gray-500'>
            {admin ? 'Tickets from all customers.' : 'Your organization\'s support requests.'}
          </p>
        </div>
        {canCreate && (
          <Link
            to='/tickets/new'
            className='rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700'
          >
            New ticket
          </Link>
        )}
      </div>

      <label className='mb-3 flex items-center gap-2 text-sm text-gray-600'>
        <input
          type='checkbox'
          checked={showClosed}
          onChange={(event) => setShowClosed(event.target.checked)}
          className='h-4 w-4'
        />
        Show closed tickets
      </label>

      {visibleTickets.length === 0
        ? (
          <p className='text-gray-500'>{showClosed ? 'No tickets yet.' : 'No open tickets.'}</p>
          )
        : (
          <table className='w-full overflow-hidden rounded-lg border border-gray-200 bg-white text-left text-sm shadow-sm'>
            <thead className='bg-gray-50'>
              <tr className='border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500'>
                <th className='p-3 font-semibold'>Urgency</th>
                <th className='p-3 font-semibold'>Status</th>
                {admin && <th className='p-3 font-semibold'>Customer</th>}
                <th className='p-3 font-semibold'>Description</th>
                <th className='p-3 font-semibold'>Created</th>
                <th className='p-3 font-semibold'>Closed</th>
              </tr>
            </thead>
            <tbody>
              {visibleTickets.map((t) => (
                <tr key={t.id} className='border-b border-gray-100 hover:bg-gray-50'>
                  <td className='p-3'>
                    <Link to={'/tickets/' + t.id} className='block'>
                      <UrgencyBadge urgency={t.urgency} />
                    </Link>
                  </td>
                  <td className='p-3'>
                    <Link to={'/tickets/' + t.id} className='block text-gray-700'>
                      {t.status}
                    </Link>
                  </td>
                  {admin && (
                    <td className='p-3'>
                      <Link to={'/tickets/' + t.id} className='block text-gray-700'>
                        {t.customerName}
                      </Link>
                    </td>
                  )}
                  <td className='p-3'>
                    <Link to={'/tickets/' + t.id} className='block text-blue-6 hover:underline'>
                      {truncate(t.description)}
                    </Link>
                  </td>
                  <td className='p-3'>
                    <Link to={'/tickets/' + t.id} className='block text-gray-500'>
                      {formatCreatedAt(t.createdAt)}
                    </Link>
                  </td>
                  <td className='p-3'>
                    <Link to={'/tickets/' + t.id} className='block text-gray-500'>
                      {t.status === 'Closed' ? formatCreatedAt(t.statusChangedAt) : ''}
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          )}
    </div>
  )
}

export default TicketsPage
